import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Wizard, Button, WidgetContainer } from '@common/components';
import { fetchWidgetData, fetchTableColumns, fetchInventorySample, validateQuery } from '@api/dataSource';
import { transformChartData } from '@common/utils/transformChartData';
import { PREVIEW_ROW_LIMIT } from '@reports/utils/dashboard/constants';
import { loadMetricValuesDataForPreview } from '@reports/utils/dashboard/loadMetricValuesDataForPreview';
import { combineSeriesData, getTableYAxisIndex } from '@reports/utils/dashboard/combineSeriesData';
import { fetchCustomQueryLineData } from '@reports/utils/dashboard/fetchCustomQueryLineData';
import { buildInventoryConfig, flattenSplitByCategoryTables } from '@reports/utils/inventoryUtils';
import { fetchStatusColorMap } from '@api/statuses';
import { applySeverityColors } from '@common/utils/severityColors';
import WidgetRenderer from '../../Widgets/WidgetRenderer';
import Step1WidgetType from './Step1WidgetType';
import Step2DataSource from './Step2DataSource';
import Step3ChartConfig from './Step3ChartConfig';
import Step3InventoryConfig from './Step3InventoryConfig';
import TextConfigurationPanel from './TextConfigurationPanel';
import {
  WIZARD_STEP_CONFIG,
  WIDGET_TYPES,
  DATA_SOURCE_TYPES,
  DEFAULT_WIDGET_CONFIG,
  isWidgetConfigComplete,
  isInventoryConfigComplete,
} from './constants';
import { ArrowUpIcon, ICON_MAP } from './icons';
import * as styles from './styles.module.css';

function WidgetWizard({ widget, onSave, onCancel }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0); // 0-based for Wizard component
  const [widgetData, setWidgetData] = useState({
    title: widget?.title || '',
    type: widget?.type || '',
    config: widget?.config || { ...DEFAULT_WIDGET_CONFIG },
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [previewWidget, setPreviewWidget] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const statusColorMapRef = useRef(null);
  useEffect(() => {
    fetchStatusColorMap().then(map => { statusColorMapRef.current = map; });
  }, []);

  // Check if current widget type is text (special handling - no data source needed)
  const isTextWidget = widgetData.type === 'text';
  // Check if data source is inventory (uses special Step 3)
  const dataSources = widgetData.config?.dataSources || [];
  const isInventoryDataSource = dataSources.some((ds) => ds.type === 'inventory');

  // Build wizard steps with components (avoids circular imports in constants.js)
  // Text widgets have a simplified flow: Step 1 (Type) -> Step 2 (Text Config)
  // Inventory data source uses Step3InventoryConfig instead of Step3ChartConfig
  const WIZARD_STEPS = useMemo(() => {
    if (isTextWidget) {
      return [
        { ...WIZARD_STEP_CONFIG[0], title: t(WIZARD_STEP_CONFIG[0].name), component: Step1WidgetType },
        { ...WIZARD_STEP_CONFIG[2], title: t(WIZARD_STEP_CONFIG[2].name), component: TextConfigurationPanel },
      ];
    }

    // Determine which Step 3 component to use based on data source
    const Step3Component = isInventoryDataSource ? Step3InventoryConfig : Step3ChartConfig;
    const step3Title = isInventoryDataSource
      ? t('dashboard.widgetWizard.steps.inventoryConfig')
      : t(WIZARD_STEP_CONFIG[2].name);

    return [
      { ...WIZARD_STEP_CONFIG[0], title: t(WIZARD_STEP_CONFIG[0].name), component: Step1WidgetType },
      { ...WIZARD_STEP_CONFIG[1], title: t(WIZARD_STEP_CONFIG[1].name), component: Step2DataSource },
      { ...WIZARD_STEP_CONFIG[2], title: step3Title, component: Step3Component },
    ];
  }, [t, isTextWidget, isInventoryDataSource]);

  // Update widget data for top-level fields
  const handleWidgetDataChange = useCallback(
    (field, value) => {
      setWidgetData((prev) => {
        const next = { ...prev, [field]: value };

        // When the widget type changes, reset datasources and their config
        // because different widget types support different datasource types
        // (e.g., inventory is table-only, metric_values is line-only).
        if (field === 'type' && value !== prev.type) {
          next.config = {
            ...DEFAULT_WIDGET_CONFIG,
            displayName: prev.config.displayName,
          };
          setAvailableColumns([]);
        }

        return next;
      });

      // Clear validation error for this field
      if (validationErrors[field]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [validationErrors],
  );

  // Update widget config (nested object) - for widget-level config like displayName
  const handleConfigChange = useCallback((field, value) => {
    setWidgetData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value,
      },
    }));
  }, []);

  // Update config inside a specific dataSource
  const handleDataSourceConfigChange = useCallback((field, value, sourceType) => {
    setWidgetData((prev) => {
      const currentDataSources = prev.config.dataSources || [];
      const updatedDataSources = currentDataSources.map((ds) => {
        if (ds.type === sourceType) {
          return {
            ...ds,
            config: {
              ...ds.config,
              [field]: value,
            },
          };
        }
        return ds;
      });
      return {
        ...prev,
        config: {
          ...prev.config,
          dataSources: updatedDataSources,
        },
      };
    });
  }, []);

  // Handle available columns from Step 2
  const handleColumnsUpdate = useCallback((columns) => {
    setAvailableColumns(columns || []);
  }, []);

  // Reset availableColumns whenever dataSources change so that the
  // Step 2→3 transition always re-fetches columns for the new selection.
  const dataSourcesKey = useMemo(
    () => (widgetData.config.dataSources || []).map((ds) => ds.id).join(','),
    [widgetData.config.dataSources],
  );
  useEffect(() => {
    setAvailableColumns([]);
  }, [dataSourcesKey]);

  // Convert API rows into widget-friendly data
  const convertToWidgetData = useCallback(
    (apiData, widgetType, config) => {
      if (!apiData || !apiData.columns || !apiData.rows) {
        return null;
      }
      const { columns, rows } = apiData;

      // Extract effective config from dataSources (type-specific config is now inside dataSource.config)
      // Check for table or custom_query data sources
      const configDataSources = config?.dataSources || [];
      const relevantSource = configDataSources.find((ds) => ds.type === 'table' || ds.type === 'custom_query');
      const effectiveConfig = relevantSource?.config || {};

      switch (widgetType) {
        case 'metric': {
          const valueIndex = columns.indexOf('value');
          if (valueIndex !== -1 && rows[0]) {
            const result = rows[0][valueIndex];
            const aggregation = effectiveConfig.aggregation || 'count';
            const labels = {
              count_distinct: 'Distinct count',
              avg: 'Average',
              min: 'Minimum',
              max: 'Maximum',
              sum: 'Sum',
              count: 'Count',
            };
            const aggregationLabel = labels[aggregation] || labels.count;
            // displayName stays at root level, but chartBy is inside dataSource config
            const subtitle =
              config.displayName ||
              (effectiveConfig.chartBy ? `${aggregationLabel} of ${effectiveConfig.chartBy}` : aggregationLabel);
            return {
              value: typeof result === 'number' ? result.toLocaleString() : result,
              subtitle,
              status: 'ok',
            };
          }
          return { value: '0', subtitle: config.displayName || t('dashboard.widgets.noData'), status: 'ok' };
        }
        case 'pie':
        case 'donut':
        case 'funnel':
        case 'treemap': {
          // Handle table-prefixed column names (e.g., effectiveConfig.chartBy = "events.metric" but column = "metric")
          const chartByColumn = effectiveConfig.chartBy?.includes('.')
            ? effectiveConfig.chartBy.split('.').pop()
            : effectiveConfig.chartBy;
          let chartByIndex = columns.indexOf(effectiveConfig.chartBy);
          if (chartByIndex === -1 && chartByColumn) {
            chartByIndex = columns.indexOf(chartByColumn);
          }
          const valueIndex = columns.indexOf('value');
          if (chartByIndex !== -1 && valueIndex !== -1) {
            return rows.map((row) => ({
              name: String(row[chartByIndex]),
              value: parseFloat(row[valueIndex]) || 0,
            }));
          }
          return null;
        }
        case 'bar':
        case 'line':
        case 'line-2d':
        case 'grouped-bar':
        case 'stacked-bar':
        case 'stacked-line':
          return transformChartData(columns, rows, config, widgetType);
        case 'table':
        case 'table-2d':
          return { columns, rows };
        default:
          return null;
      }
    },
    [t],
  );

  // Load preview data based on current wizard state
  const loadPreview = useCallback(async () => {
    if (!showPreview) return;
    setPreviewError(null);

    if (!widgetData.type) {
      setPreviewWidget(null);
      setPreviewError(null); // Don't show error, show placeholder
      return;
    }

    // Text widgets don't need data - just show the text content directly
    if (widgetData.type === 'text') {
      const widgetForPreview = {
        ...widgetData,
        id: widget?.id || 'preview-widget',
        data: null, // Text widget doesn't use data property
      };
      setPreviewWidget(widgetForPreview);
      setPreviewLoading(false);
      return;
    }

    const previewDataSources = widgetData.config?.dataSources || [];
    const customQuerySource = previewDataSources.find((ds) => ds.type === 'custom_query');
    const hasCustomQuery = !!customQuerySource;
    // Query is inside dataSource.config.query for custom_query type
    const hasQuery = customQuerySource?.config?.query?.trim();

    // Validate data sources for preview
    if (hasCustomQuery && !hasQuery) {
      setPreviewWidget(null);
      setPreviewError(null); // Don't show error, show placeholder
      return;
    }
    if (previewDataSources.length === 0) {
      setPreviewWidget(null);
      setPreviewError(null); // Don't show error, show placeholder
      return;
    }

    setPreviewLoading(true);
    try {
      const widgetForPreview = {
        ...widgetData,
        id: widget?.id || 'preview-widget',
        data: null,
        previewLimit: PREVIEW_ROW_LIMIT,
      };

      // For line charts, handle multiple data sources (metric_values, table, custom_query)
      if (widgetData.type === 'line') {
        const metricValuesSource = previewDataSources.find((ds) => ds.type === 'metric_values');
        const tableSources = previewDataSources.filter((ds) => ds.type === 'table');
        const allSeriesData = [];

        // 1. Fetch metric_values data if selected
        // Uses /timeseries/sample endpoint which returns entities with actual data
        if (metricValuesSource) {
          const metricValuesData = await loadMetricValuesDataForPreview(widgetForPreview, 3);
          if (metricValuesData?.series) {
            // Add yAxisIndex=0 (left axis) for metric_values series
            // Store categories from metric_values as primary categories
            allSeriesData.push(
              ...metricValuesData.series.map((s, idx) => ({
                ...s,
                yAxisIndex: 0,
                categories: idx === 0 ? metricValuesData.categories : undefined,
                isMetricValues: true,
              })),
            );
          }
        }

        // 2. Fetch table data for each table source (in parallel)
        // Config fields (timeColumn, yAxisField, groupBy) are now at root level
        const tableDataPromises = tableSources.map(async (tableSource, sourceIndex) => {
          try {
            const tableConfig = {
              ...widgetData.config,
              table: tableSource.id,
              // timeColumn, yAxisField, groupBy are already in widgetData.config
            };

            const response = await fetchWidgetData('line', tableConfig, {}, true, PREVIEW_ROW_LIMIT);

            if (response.status === 'success' && response.data?.rows?.length) {
              const tableData = convertToWidgetData(response.data, 'line', tableConfig);
              if (tableData) {
                const yAxisIndex = getTableYAxisIndex(!!metricValuesSource, sourceIndex);
                if (tableData.series) {
                  return tableData.series.map((s, idx) => ({
                    ...s,
                    name: `${tableSource.id}: ${s.name}`,
                    categories: idx === 0 ? tableData.categories : undefined,
                    yAxisIndex,
                  }));
                }
                if (tableData.categories && tableData.values) {
                  return [
                    {
                      name: tableSource.id,
                      data: tableData.values,
                      categories: tableData.categories,
                      yAxisIndex,
                    },
                  ];
                }
              }
            }
            return [];
          } catch {
            return [];
          }
        });

        const tableResults = await Promise.all(tableDataPromises);
        tableResults.forEach((series) => allSeriesData.push(...series));

        // 3. Fetch custom query data if selected (line chart + custom query)
        const customQuerySeries = await fetchCustomQueryLineData({
          customQuerySource,
          widgetConfig: widgetData.config,
          convertFn: convertToWidgetData,
          hasMetricValues: !!metricValuesSource,
          tableSourceCount: tableSources.length,
          previewRowLimit: PREVIEW_ROW_LIMIT,
        });
        customQuerySeries.forEach((s) => allSeriesData.push(s));

        // Combine all series data using shared utility
        if (allSeriesData.length > 0) {
          let combinedData = combineSeriesData(allSeriesData, {
            metricValuesSource,
            tableSources: customQuerySource
              ? [...tableSources, customQuerySource]
              : tableSources,
          });
          if (statusColorMapRef.current) {
            combinedData = applySeverityColors(combinedData, widgetData.type, widgetData.config, statusColorMapRef.current);
          }
          widgetForPreview.data = combinedData;
          setPreviewWidget(widgetForPreview);
        } else {
          setPreviewWidget(null);
          setPreviewError(t('dashboard.widgets.noData'));
        }
        setPreviewLoading(false);
        return;
      }

      // MON-17365: Handle inventory data source preview
      // Uses /inventory/sample endpoint - similar to timeseries/sample pattern
      // Fetches sample hosts with inventory data (RLS/permissions applied on backend)
      const inventorySource = previewDataSources.find((ds) => ds.type === 'inventory');
      if (inventorySource) {
        // Build inventory config using shared utility (isDataSourceConfig=true since config comes from dataSource.config)
        const inventoryConfig = buildInventoryConfig(inventorySource.config || {}, true);

        // Use sample endpoint with 3 hosts (same pattern as timeseries preview)
        const inventoryResponse = await fetchInventorySample(inventoryConfig, 3);

        if (inventoryResponse.status === 'success' && inventoryResponse.data) {
          const { columns, rows, total, tables } = inventoryResponse.data;

          // Handle split_by_category layout - flatten to single table for preview
          if (inventoryConfig.inventoryTableLayout === 'split_by_category' && tables) {
            const flatData = flattenSplitByCategoryTables(tables);
            // Limit rows for preview (backend already limits hosts, but limit total rows too)
            const limitedRows = flatData.rows.slice(0, PREVIEW_ROW_LIMIT);
            widgetForPreview.data = { columns: flatData.columns, rows: limitedRows, total: flatData.total };
          } else if (rows && columns) {
            // Standard layouts: long_form, pivot, aggregated, expanded
            // Limit rows for preview (backend limits hosts, frontend limits total rows)
            const limitedRows = rows.slice(0, PREVIEW_ROW_LIMIT);
            widgetForPreview.data = { columns, rows: limitedRows, total };
          }

          if (widgetForPreview.data) {
            setPreviewWidget(widgetForPreview);
          } else {
            setPreviewWidget(null);
            setPreviewError(t('dashboard.widgets.noData'));
          }
        } else {
          setPreviewWidget(null);
          setPreviewError(inventoryResponse.message || t('dashboard.widgets.noData'));
        }
        setPreviewLoading(false);
        return;
      }

      // For non-line charts, use single data source
      const singleSource = previewDataSources[0];
      if (!singleSource || (singleSource.type === 'custom_query' && !hasQuery)) {
        setPreviewWidget(null);
        setPreviewError(null);
        setPreviewLoading(false);
        return;
      }

      // Build config for the data source
      const sourceConfig = {
        ...widgetData.config,
        table: singleSource.type === 'table' ? singleSource.id : '',
      };

      // Use server-side query building for secure data fetching
      const response = await fetchWidgetData(
        widgetData.type,
        sourceConfig,
        {}, // No dashboard filters in preview
        true, // In edit mode
        PREVIEW_ROW_LIMIT,
      );
      if (response.status === 'success' && response.data?.rows?.length) {
        const limitedData = {
          ...response.data,
          rows: response.data.rows.slice(0, PREVIEW_ROW_LIMIT),
        };
        let previewData = convertToWidgetData(limitedData, widgetData.type, widgetData.config);
        if (previewData && statusColorMapRef.current) {
          previewData = applySeverityColors(previewData, widgetData.type, widgetData.config, statusColorMapRef.current);
        }
        widgetForPreview.data = previewData;
        setPreviewWidget(widgetForPreview);
      } else {
        setPreviewWidget(null);
        setPreviewError(t('dashboard.widgets.noData'));
      }
    } catch (err) {
      setPreviewWidget(null);
      setPreviewError(err.message || t('dashboard.widgets.noData'));
    } finally {
      setPreviewLoading(false);
    }
  }, [showPreview, widgetData, widget, convertToWidgetData, t]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // Validate current step
  const validateCurrentStep = useCallback(() => {
    const errors = {};

    if (currentStep === 0) {
      if (!widgetData.title.trim()) {
        errors.title = t('dashboard.widgetWizard.step1.validation.nameRequired');
      }
      if (!widgetData.type) {
        errors.type = t('dashboard.widgetWizard.step1.validation.typeRequired');
      }
    }

    // For text widgets, step 1 is text configuration (no required fields beyond text content)
    if (currentStep === 1 && isTextWidget) {
      // Text content is optional - user can have empty text widget
      return errors;
    }

    if (currentStep === 1 && !isTextWidget) {
      const configDataSources = widgetData.config.dataSources || [];
      const customQuerySource = configDataSources.find((ds) => ds.type === 'custom_query');
      const hasInventorySource = configDataSources.some((ds) => ds.type === 'inventory');

      // Must have at least one data source selected
      if (configDataSources.length === 0) {
        errors.dataSource = t('dashboard.widgetWizard.step2.validation.dataSourceRequired');
      }
      // Custom query must have query text (query is now inside dataSource.config)
      if (customQuerySource && !customQuerySource.config?.query?.trim()) {
        errors.dataSource = t('dashboard.widgetWizard.step2.validation.queryRequired');
      }
      // Inventory data source is only allowed for table widgets
      if (hasInventorySource && widgetData.type !== 'table') {
        errors.dataSource = t('dashboard.widgetWizard.step2.validation.inventoryOnlyForTable');
      }
    }

    // Step 3 validation for inventory configuration (FR-10 UI Guardrails)
    if (currentStep === 2 && isInventoryDataSource) {
      const configDataSources = widgetData.config.dataSources || [];
      const inventorySource = configDataSources.find((ds) => ds.type === 'inventory');
      const inventoryConfig = inventorySource?.config || {};

      // Row Type is required
      if (!inventoryConfig.rowType) {
        errors.inventoryRowType = t('dashboard.widgetWizard.inventory.validation.rowTypeRequired');
      }

      // Table Layout is required
      if (!inventoryConfig.tableLayout) {
        errors.inventoryTableLayout = t('dashboard.widgetWizard.inventory.validation.layoutRequired');
      }

      // Pivot layout is only allowed when rows = Hosts
      if (inventoryConfig.tableLayout === 'pivot' && inventoryConfig.rowType !== 'hosts') {
        errors.inventoryTableLayout = t('dashboard.widgetWizard.inventory.validation.pivotOnlyForHosts');
      }

      // If "specific categories" is selected, at least one category must be chosen
      if (
        inventoryConfig.categoryHandling === 'specific' &&
        (!inventoryConfig.selectedCategories || inventoryConfig.selectedCategories.length === 0)
      ) {
        errors.inventorySelectedCategories = t('dashboard.widgetWizard.inventory.validation.selectAtLeastOneCategory');
      }

      // Pivot column limit validation
      if (inventoryConfig.tableLayout === 'pivot') {
        const limit = inventoryConfig.pivotColumnLimit || 20;
        if (limit < 1 || limit > 50) {
          errors.inventoryPivotColumnLimit = t('dashboard.widgetWizard.inventory.validation.pivotColumnLimitRange');
        }
      }
    }

    return errors;
  }, [currentStep, widgetData, t, isTextWidget, isInventoryDataSource]);

  // Navigate to previous step
  const handleBack = useCallback(() => {
    // Just clear errors when going back
    setValidationErrors({});
  }, []);

  // Fetch columns for table data sources — used before transitioning to Step 3
  const fetchColumnsForDataSources = useCallback(async () => {
    const configDataSources = widgetData.config.dataSources || [];
    const tableIds = configDataSources
      .filter((ds) => ds.type === DATA_SOURCE_TYPES.TABLE)
      .map((ds) => ds.id);

    if (tableIds.length === 0) return;

    const columnPromises = tableIds.map((tableName) => fetchTableColumns(tableName));
    const responses = await Promise.all(columnPromises);

    const allColumns = [];
    responses.forEach((response, index) => {
      if (response.status === 'success' && response.data) {
        const tableName = tableIds[index];
        const prefixedColumns = response.data.map((col) => ({
          ...col,
          name: `${tableName}.${col.name}`,
          table: tableName,
        }));
        allColumns.push(...prefixedColumns);
      }
    });

    setAvailableColumns(allColumns);
  }, [widgetData.config.dataSources]);

  // Navigate to next step with validation.
  // Returns a Promise when moving from Step 2→3 so the Wizard shows a loading
  // state on the Next button while columns are being fetched.
  const handleNext = useCallback(() => {
    const errors = validateCurrentStep();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false; // Prevent navigation
    }

    setValidationErrors({});

    const isLeavingDataSourceStep = currentStep === 1 && !isTextWidget;
    if (isLeavingDataSourceStep && availableColumns.length === 0) {
      const configDataSources = widgetData.config.dataSources || [];
      const hasTableSources = configDataSources.some((ds) => ds.type === DATA_SOURCE_TYPES.TABLE);
      const customQuerySource = configDataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.CUSTOM_QUERY);

      if (hasTableSources) {
        return fetchColumnsForDataSources().then(() => true);
      }

      if (customQuerySource?.config?.query?.trim()) {
        return validateQuery(customQuerySource.config.query)
          .then((response) => {
            if (response.status === 'success' && response.data?.columns) {
              setAvailableColumns(response.data.columns);
            }
            return true;
          })
          .catch(() => true);
      }
    }

    return true;
  }, [validateCurrentStep, currentStep, isTextWidget, widgetData.config.dataSources, availableColumns, fetchColumnsForDataSources]);

  // Save widget (final step)
  const handleFinish = useCallback(() => {
    const errors = validateCurrentStep();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSave(widgetData);
  }, [widgetData, validateCurrentStep, onSave]);

  // Get button text based on context
  const getButtonText = useCallback(() => {
    if (currentStep === WIZARD_STEPS.length - 1) {
      return widget ? t('dashboard.widgetWizard.saveWidget') : t('dashboard.widgetWizard.addWidget');
    }
    return t('dashboard.widgetWizard.addWidget');
  }, [currentStep, widget, t, WIZARD_STEPS.length]);

  // Step data passed to all step components
  const stepData = useMemo(
    () => ({
      widgetData,
      onWidgetDataChange: handleWidgetDataChange,
      onConfigChange: handleConfigChange,
      onDataSourceConfigChange: handleDataSourceConfigChange,
      onColumnsUpdate: handleColumnsUpdate,
      validationErrors,
      widgetTypes: WIDGET_TYPES,
      availableColumns,
    }),
    [
      widgetData,
      handleWidgetDataChange,
      handleConfigChange,
      handleDataSourceConfigChange,
      handleColumnsUpdate,
      validationErrors,
      availableColumns,
    ],
  );

  // Custom footer content (just the Show Preview button)
  const footerContent = (
    <div className={styles.previewButtonWrapper}>
      <Button
        variant="outline"
        size="small"
        onClick={() => setShowPreview(!showPreview)}
        className={styles.previewToggleButton}
      >
        <ArrowUpIcon className={showPreview ? styles.iconRotated : ''} />
        {showPreview ? t('dashboard.widgetWizard.hidePreview') : t('dashboard.widgetWizard.showPreview')}
      </Button>
    </div>
  );

  // Get preview placeholder message based on current state
  const getPreviewPlaceholder = () => {
    if (!widgetData.type) {
      return {
        showEyeIcon: true,
        message:
          t('dashboard.widgetWizard.preview.selectVisualization') || 'Select a visualization type to see a preview',
      };
    }

    // Get widget type info
    const widgetType = WIDGET_TYPES.find((w) => w.value === widgetData.type);
    const widgetLabel = widgetType?.label || widgetData.type.toUpperCase();

    // Text widgets always show preview (even with empty content)
    if (widgetData.type === 'text') {
      return null; // No placeholder needed, show actual widget
    }

    const placeholderDataSources = widgetData.config?.dataSources || [];
    const placeholderCustomQuerySource = placeholderDataSources.find((ds) => ds.type === 'custom_query');
    const placeholderTableSource = placeholderDataSources.find((ds) => ds.type === 'table');
    const placeholderInventorySource = placeholderDataSources.find((ds) => ds.type === 'inventory');

    // No data sources selected
    if (placeholderDataSources.length === 0) {
      return {
        showWidgetIcon: true,
        widgetType: widgetData.type,
        widgetLabel,
        message: t('dashboard.widgetWizard.preview.configureDataSource'),
      };
    }

    // Custom query without query text (query is now inside dataSource.config)
    if (placeholderCustomQuerySource && !placeholderCustomQuerySource.config?.query?.trim()) {
      return {
        showWidgetIcon: true,
        widgetType: widgetData.type,
        widgetLabel,
        message: t('dashboard.widgetWizard.preview.configureDataSource'),
      };
    }

    // Check if configuration is complete based on widget type and data source
    const hasColumnBasedSource = !!(placeholderTableSource || placeholderCustomQuerySource);
    const dsConfig = placeholderTableSource?.config || placeholderCustomQuerySource?.config || {};

    // For table/custom_query sources, check all required fields based on widget type
    if (hasColumnBasedSource && !isWidgetConfigComplete(widgetData.type, dsConfig, hasColumnBasedSource)) {
      return {
        showWidgetIcon: true,
        widgetType: widgetData.type,
        widgetLabel,
        message: t('dashboard.widgetWizard.preview.configureDataSource'),
      };
    }

    // For inventory data source, check required fields
    if (placeholderInventorySource && !isInventoryConfigComplete(placeholderInventorySource.config)) {
      return {
        showWidgetIcon: true,
        widgetType: widgetData.type,
        widgetLabel,
        message: t('dashboard.widgetWizard.preview.configureDataSource'),
      };
    }

    // metric_values has sensible defaults, considered configured when selected
    // (only for line charts, and has default groupBy, aggregateLiteral, aggregateFunction)

    return null;
  };

  const previewPlaceholder = getPreviewPlaceholder();

  return (
    <>
      <Wizard
        steps={WIZARD_STEPS}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        title={widget ? t('dashboard.widgetWizard.editTitle') : t('dashboard.widgetWizard.createTitle')}
        subtitle={widgetData.title || t('common.name')}
        onClose={onCancel}
        closeButtonText={t('dashboard.widgetWizard.closePage')}
        nextButtonText={t('common.next')}
        nextLoadingText={t('common.loading')}
        backButtonText={t('common.back')}
        completeButtonText={getButtonText()}
        onNext={handleNext}
        onBack={handleBack}
        onComplete={handleFinish}
        stepData={stepData}
        footer={footerContent}
        fullScreen
        showSidebar
        className={styles.widgetWizard}
      />

      {showPreview && (
        <div className={styles.previewPanel}>
          <div className={styles.previewButtonContainer}>
            <Button
              variant="outline"
              size="small"
              onClick={() => setShowPreview(false)}
              className={styles.hidePreviewButton}
            >
              <ArrowUpIcon className={styles.iconRotated} />
              {t('dashboard.widgetWizard.hidePreview')}
            </Button>
          </div>

          <div className={styles.previewInnerContainer}>
            <div className={styles.previewHeader}>
              <h3 className={styles.previewTitle}>{widgetData.title || t('common.name')}</h3>
              <p className={styles.previewSubtitle}>{t('dashboard.widgetWizard.preview.subtitle')}</p>
            </div>

            <div className={styles.previewContent}>
              <WidgetContainer
                loading={previewLoading}
                error={previewError}
                noData={
                  !previewLoading &&
                  !previewError &&
                  !previewPlaceholder &&
                  !previewWidget?.data &&
                  previewWidget?.type !== 'text'
                }
                noDataMessage={t('dashboard.widgets.noData')}
              >
                {!previewLoading && !previewError && previewPlaceholder && (
                  <div className={styles.previewPlaceholder}>
                    {previewPlaceholder.showEyeIcon && (
                      <>
                        <div className={styles.eyeIconContainer}>
                          <svg
                            className={styles.eyeIcon}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 12.5C2.73 8.11 7 5 12 5C17 5 21.27 8.11 23 12.5C21.27 16.89 17 20 12 20C7 20 2.73 16.89 1 12.5Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="12"
                              cy="12.5"
                              r="3.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className={styles.placeholderMessage}>{previewPlaceholder.message}</div>
                      </>
                    )}
                    {previewPlaceholder.showWidgetIcon && (
                      <>
                        <div className={styles.widgetIconContainer}>
                          {(() => {
                            const IconComponent = ICON_MAP[previewPlaceholder.widgetType];
                            return IconComponent ? <IconComponent className={styles.widgetIcon} /> : null;
                          })()}
                        </div>
                        <div className={styles.widgetTypeLabel}>{previewPlaceholder.widgetLabel}</div>
                        <div className={styles.placeholderMessage}>{previewPlaceholder.message}</div>
                      </>
                    )}
                  </div>
                )}

                {!previewLoading &&
                  !previewError &&
                  !previewPlaceholder &&
                  (previewWidget?.data || previewWidget?.type === 'text') && (
                    <div className={styles.previewWidget}>
                      <WidgetRenderer
                        widget={previewWidget}
                        dashboardArgs={{}}
                        dashboardFilters={{}}
                        isEditing={false}
                      />
                    </div>
                  )}
              </WidgetContainer>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

WidgetWizard.propTypes = {
  widget: PropTypes.shape({
    title: PropTypes.string,
    type: PropTypes.string,
    config: PropTypes.shape({}),
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

WidgetWizard.defaultProps = {
  widget: null,
};

export default WidgetWizard;
